/**
 * T8 — Distance-based progressive LOD manager.
 *
 * Given a camera, a scene full of Three.Mesh objects, and a per-mesh index
 * of PLOTEX_progressive metadata, the manager picks an appropriate LOD for
 * each mesh on every update tick and schedules worker parses + geometry
 * swaps to match. "Appropriate" here means: closer meshes get finer LODs,
 * distant meshes get coarser ones.
 *
 * This is the distance-based baseline. T9 replaces the scoring function
 * with a screen-space coverage metric; T10 adds FPS-adaptive throttling.
 * Everything else about the pipeline stays the same so those upgrades are
 * drop-in.
 *
 * Architecture:
 *
 *   update() — called once per frame by the runtime animate loop
 *     └── throttle: only recompute desired levels every UPDATE_INTERVAL ms
 *     └── for each managed mesh:
 *           pickLevel(mesh) → desired integer level
 *           if desired != current and nothing is in-flight for this mesh:
 *             schedule a parse via the worker pool
 *     └── a handful of in-flight fetches at a time (MAX_INFLIGHT) so
 *         we don't queue 500 network requests when the camera teleports
 *
 *   onParsed — worker postMessage handler
 *     └── find the matching MeshState
 *     └── applyLod(mesh, parsedData)
 *     └── currentLevel := requestedLevel
 */

import {
  BufferAttribute,
  BufferGeometry,
  Camera,
  InstancedMesh,
  Matrix4,
  Mesh,
  PerspectiveCamera,
  Quaternion,
  Vector3,
} from 'three';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type PlotexLodEntry = {
  path: string;
  level: number;
  vertexCount: number;
  indexCount: number;
  density: number;
};

export type PlotexPriority = 'high' | 'normal' | 'low';

export type PlotexProgressiveData = {
  guid: string;
  /**
   * LOD level whose simplified geometry is already baked into scene.glb
   * for this mesh. -1 means the mesh ships at its original full quality
   * (sensitive meshes are never decimated inline). The runtime uses
   * this as the initial `currentLevel` so the picker only fetches a
   * finer LOD when the mesh grows on screen.
   */
  inlineLevel: number;
  /**
   * P1b — runtime picker quality floor. The picker will never choose
   * a LOD coarser (higher level number) than this. 0 means no floor —
   * the mesh can upgrade all the way to full quality. Sourced from
   * PLOTEX_authoring.lod_min in Blender; defaults to 0 if absent.
   */
  minLevel: number;
  /**
   * P1b — fetch-queue ordering. `high` priority meshes are scheduled
   * first; `low` priority are deferred until the rest of the scene
   * has converged. Impostor-source meshes auto-promote to 'high'
   * unless the author explicitly set 'low'.
   */
  priority: PlotexPriority;
  /**
   * P2b — optional override for the level-0 fetch URL. When the Blender
   * plugin emits a `mesh_<guid>.glb` chunk and stamps PLOTEX_mesh_ref on
   * the node, the runtime prefers this path over the lods[level=0]
   * entry. Backward compatible: undefined means "use lods[].path[0]".
   */
  baseMeshPath?: string;
  lods: PlotexLodEntry[];
};

export type PlotexIndex = Map<Mesh, PlotexProgressiveData>;

type ParsedLodNode = {
  translation: [number, number, number];
  rotation: [number, number, number, number];
  scale: [number, number, number];
};

type ParsedLod = {
  indices: Uint16Array | Uint32Array;
  attributes: Record<
    string,
    { array: ArrayBufferView; itemSize: number; componentType: number; normalized: boolean }
  >;
  node: ParsedLodNode;
  meshName: string;
  quantized: boolean;
};

type MeshState = {
  mesh: Mesh;
  meta: PlotexProgressiveData;
  /** The LOD level this mesh is currently using, or -1 for the original inline geometry. */
  currentLevel: number;
  /** Level currently being fetched, or -1 if nothing is in flight. */
  inFlightLevel: number;
  /** Cached world-space center so we don't recompute it every frame. */
  center: Vector3;
  /** Cached world-space bounding radius for the coverage calculation in T9. */
  radius: number;
  /**
   * InstancedMesh support: world-space centers of every instance. When
   * present, the picker uses the CLOSEST instance to the camera for
   * distance + coverage math instead of `center`. This is critical for
   * things like a row of 52 palm trees — the geometry's bounding sphere
   * represents ONE instance in local space, so without per-instance
   * centers the picker thinks all palms are stacked at the origin.
   */
  instanceCenters?: Vector3[];
};

type Pending = {
  resolve: (v: ParsedLod) => void;
  reject: (e: Error) => void;
  meshState: MeshState;
  requestedLevel: number;
};

/* -------------------------------------------------------------------------- */
/* Tunables                                                                   */
/* -------------------------------------------------------------------------- */

const UPDATE_INTERVAL_MS = 150;
const MAX_INFLIGHT = 6;

/**
 * T10 FPS-adaptive throttling.
 *
 * We track a smoothed frame time (exponentially-weighted moving average)
 * and back off LOD scheduling when the average drops below FPS_TARGET.
 * The backoff is a cooldown timer: once FPS recovers we still wait
 * FPS_RECOVERY_MS before enqueueing new work, so one-off frame spikes
 * don't cause oscillation between "scheduling bursts" and "stalled".
 */
const FPS_TARGET = 45;           // minimum acceptable FPS
const FPS_EWMA_ALPHA = 0.1;      // smoothing factor: higher = more reactive
const FPS_RECOVERY_MS = 500;     // cooldown after a low-FPS event

/**
 * Triangle-per-pixel target for the coverage-based LOD picker (T9).
 *
 * A value of 0.5 means "aim for one triangle per two pixels of screen
 * coverage". Lower → coarser LODs (faster), higher → finer LODs (prettier).
 * 0.5 is the rough sweet spot for architectural scenes at 1080p: detailed
 * silhouettes remain crisp while the far background decimates aggressively.
 */
const TRIS_PER_PIXEL_TARGET = 0.5;

/** Minimum screen-pixel radius before we bother upgrading past L5. */
const MIN_SCREEN_RADIUS_PX = 2;

/**
 * Backoff cadence for failed (guid, level) keys. A failure parks the
 * key in `failedKeys` for FAIL_BACKOFF_MS, then we let one retry
 * through; if THAT also fails the parking time doubles, capped at
 * FAIL_BACKOFF_MAX_MS. Successful fetch clears the key + attempt
 * counter. The user explicitly asked for retry-on-missing-file to
 * keep working (drop the chunk into the right folder later, picker
 * picks it up automatically), so we never give up — only slow down.
 */
const FAIL_BACKOFF_MS = 5000;
const FAIL_BACKOFF_MAX_MS = 60_000;

/* -------------------------------------------------------------------------- */
/* Manager                                                                    */
/* -------------------------------------------------------------------------- */

export class PlotexLodManager {
  private readonly workers: Worker[];
  private readonly states: MeshState[] = [];
  private readonly pending = new Map<number, Pending>();
  private nextReqId = 0;
  private nextWorker = 0;
  private inflight = 0;
  private lastUpdate = 0;
  private disposed = false;

  // T19: per-guid+level cache of already-built BufferGeometries, plus a
  // map of waiters (other MeshStates that want the same file but arrived
  // while it was in flight). Prevents BOTH duplicate network fetches and
  // duplicate GPU uploads when a scene has 50 palm-tree instances
  // sharing one `mesh_lod_0_<guid>.glb` file — the first fetch produces
  // one BufferGeometry, and every subsequent instance re-uses it.
  //
  //   geometryCache.get(`${guid}_${level}`)      → shared BufferGeometry
  //   inFlightByKey.get(`${guid}_${level}`)      → MeshState[] waiters
  private readonly geometryCache = new Map<string, BufferGeometry>();
  private readonly inFlightByKey = new Map<string, MeshState[]>();

  // Backoff cache for failed fetches. After ANY failure (HTTP 404,
  // bad GLB header, parse exception) we record `key → retryAfter`.
  // scheduleFetch refuses to dispatch a new fetch for that key until
  // performance.now() ≥ retryAfter. Without this, a missing chunk
  // turns into an infinite retry storm: picker re-picks the same
  // level every UPDATE_INTERVAL, scheduleFetch fires, fails, the
  // state resets, picker re-picks, repeat. With it, the first
  // failure earns FAIL_BACKOFF_MS of silence; subsequent failures
  // double the backoff (capped at FAIL_BACKOFF_MAX_MS) so a server
  // outage doesn't keep hammering the network. Keys clear from the
  // map automatically the moment a fetch eventually succeeds.
  private readonly failedKeys = new Map<string, number>();
  private readonly failedKeyAttempts = new Map<string, number>();

  // T10: FPS tracking
  private lastFrameTime = 0;
  private ewmaFrameMs = 16.67; // start optimistic at ~60fps
  private cooldownUntil = 0;

  // Wave 5 Task 1E — runtime-tunable LOD controls. Seeded from the
  // module-level defaults so legacy call sites still behave identically;
  // configure() swaps individual values when a PLOTEX_project extension
  // on scene.glb (or an explicit options object from createPlotexEngine)
  // asks for different runtime behaviour.
  private updateIntervalMs = UPDATE_INTERVAL_MS;
  private maxInflight = MAX_INFLIGHT;
  private fpsTarget = FPS_TARGET;
  private trisPerPixelTarget = TRIS_PER_PIXEL_TARGET;
  private minScreenRadiusPx = MIN_SCREEN_RADIUS_PX;

  // Per-session usage counter — `${guid}_${level}` → number of times we
  // actually issued a network fetch for it. Combined with the catalog
  // size on disk, this answers "how many of our 200+ chunks did this
  // session actually use?" without instrumenting the dev tools network
  // panel by hand. Read via mgr.usageReport().
  private readonly fetchUsage = new Map<string, number>();

  // Scratch objects reused every update to avoid per-frame allocation.
  private readonly tmpCamPos = new Vector3();

  constructor(index: PlotexIndex, poolSize: number) {
    // Build MeshState list with cached centers/radii.
    for (const [mesh, meta] of index) {
      // Make sure world matrix is up to date before we read position bounds.
      mesh.updateWorldMatrix(true, false);
      if (!mesh.geometry.boundingSphere) mesh.geometry.computeBoundingSphere();
      const bs = mesh.geometry.boundingSphere!;
      const center = bs.center.clone().applyMatrix4(mesh.matrixWorld);
      // For a uniformly-scaled node (what gltf-transform produces) the
      // world radius is just radius * max-scale. Good enough for bucketing.
      const scaleVec = new Vector3();
      mesh.matrixWorld.decompose(new Vector3(), new Quaternion(), scaleVec);
      const radius = bs.radius * Math.max(scaleVec.x, scaleVec.y, scaleVec.z);
      // T24: seed currentLevel from the pipeline's inlineLevel so the
      // picker knows scene.glb already ships this mesh at level N.
      // Runtime only fetches LOD files for levels finer than N.
      const startingLevel =
        typeof meta.inlineLevel === 'number' ? meta.inlineLevel : -1;

      const state: MeshState = {
        mesh,
        meta,
        currentLevel: startingLevel,
        inFlightLevel: -1,
        center,
        radius,
      };

      // InstancedMesh fix: the geometry's bounding sphere only describes
      // ONE instance in local space. Without per-instance world centers
      // the picker thinks all 52 palm-tree instances are stacked at the
      // origin — then treats the whole cluster as a single distant blob
      // and locks them to the coarsest LOD. Walk every instance, pull
      // its world position out of the instance matrix, and cache it so
      // the picker can use the CLOSEST instance for distance math.
      //
      // There's a subtlety specific to the `gltf-transform instance`
      // pass: the baked geometry can live in a LARGE coordinate frame
      // (e.g. palm vertices at ~160m from origin), and each instance
      // matrix carries a tiny compensating scale (e.g. 0.03 — a /33x
      // shrink). So the effective per-instance world radius is
      // `bs.radius * instance_scale`, NOT `bs.radius` itself. Using the
      // raw bs.radius inflates the screen-space coverage by ~30× and
      // makes every far-away palm upgrade to L0 all the time. Read
      // the instance scale off each instance matrix, take the maximum
      // (worst-case pixel coverage), and use that as the world radius
      // for picker math.
      if ((mesh as any).isInstancedMesh) {
        const im = mesh as InstancedMesh;
        const tmpMat = new Matrix4();
        const tmpT = new Vector3();
        const tmpQ = new Quaternion();
        const tmpS = new Vector3();
        const centers: Vector3[] = [];
        const localCenter = bs.center.clone();
        let maxInstScale = 0;
        for (let i = 0; i < im.count; i++) {
          im.getMatrixAt(i, tmpMat);
          tmpMat.decompose(tmpT, tmpQ, tmpS);
          const s = Math.max(tmpS.x, tmpS.y, tmpS.z);
          if (s > maxInstScale) maxInstScale = s;
          // Compose world = parent.matrixWorld * instanceMatrix for the
          // local bounding-sphere center. applyMatrix4 is left-multiply,
          // so chain instance first, then parent.
          const worldPos = localCenter
            .clone()
            .applyMatrix4(tmpMat)
            .applyMatrix4(mesh.matrixWorld);
          centers.push(worldPos);
        }
        state.instanceCenters = centers;
        if (maxInstScale > 0) {
          // Override the cluster-scale radius we computed above with
          // the per-instance effective radius. maxInstScale already
          // lives in local units, then multiply by the parent world
          // scale (almost always 1 for gltf-transform output, but be
          // safe).
          const parentScale = Math.max(scaleVec.x, scaleVec.y, scaleVec.z);
          state.radius = bs.radius * maxInstScale * parentScale;
        }
        console.log(
          `[plotex-lod] instanced ${meta.guid.slice(0, 8)}: ${im.count} instances, ` +
          `spread=${this.boundsSpread(centers).toFixed(1)}m, ` +
          `radius=${state.radius.toFixed(2)}m`
        );
      }

      this.states.push(state);
    }

    // Spin up the worker pool.
    this.workers = [];
    for (let i = 0; i < poolSize; i++) {
      const w = new Worker(
        new URL('../workers/lod-parser.worker.ts', import.meta.url),
        { type: 'module' }
      );
      w.onmessage = (e) => this.onWorkerMessage(e);
      w.onerror = (e) => console.error('[plotex-lod] worker error:', e.message, e);
      this.workers.push(w);
    }

    // P1b: sort states by author-set priority so high-priority meshes
    // appear earlier in the scheduling loop — they get their worker
    // slots first when inflight budget is tight. Stable sort preserves
    // insertion order within a priority bucket (good for repeatability).
    const rank: Record<PlotexPriority, number> = { high: 0, normal: 1, low: 2 };
    this.states.sort(
      (a, b) => rank[a.meta.priority] - rank[b.meta.priority]
    );

    console.log(
      `[plotex-lod] manager ready — ${this.states.length} meshes, ${poolSize} workers`
    );
  }

  /** Called every frame. Cheap: returns immediately unless UPDATE_INTERVAL elapsed. */
  update(camera: Camera, now: number): void {
    if (this.disposed) return;

    // T10: track per-frame dt via EWMA for adaptive throttling. This runs
    // every frame (the cheap path), separately from the expensive scheduling
    // logic that only runs every UPDATE_INTERVAL_MS.
    if (this.lastFrameTime > 0) {
      const dt = now - this.lastFrameTime;
      // Clamp against absurd values (tab switch, breakpoint) so a single
      // 10-second pause doesn't wreck the EWMA.
      const clampedDt = Math.min(dt, 100);
      this.ewmaFrameMs =
        this.ewmaFrameMs * (1 - FPS_EWMA_ALPHA) + clampedDt * FPS_EWMA_ALPHA;
    }
    this.lastFrameTime = now;

    if (now - this.lastUpdate < this.updateIntervalMs) return;
    this.lastUpdate = now;

    // T10: check if we're under the FPS target. If so, arm a cooldown
    // and skip scheduling new work until it expires.
    const currentFps = 1000 / Math.max(0.1, this.ewmaFrameMs);
    if (currentFps < this.fpsTarget) {
      this.cooldownUntil = now + FPS_RECOVERY_MS;
      return;
    }
    if (now < this.cooldownUntil) return;

    this.tmpCamPos.setFromMatrixPosition(camera.matrixWorld);

    // Precompute camera-derived constants for the coverage calculation.
    // For a non-perspective camera (ortho), coverage math degenerates — we
    // fall back to "infinite" coverage which picks the finest LOD.
    let halfFovTan = 0;
    let viewportHeight = 0;
    if ((camera as PerspectiveCamera).isPerspectiveCamera) {
      const persp = camera as PerspectiveCamera;
      halfFovTan = Math.tan((persp.fov * Math.PI) / 180 / 2);
      viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;
    }

    // Walk states and enqueue upgrades/downgrades until we hit MAX_INFLIGHT.
    //
    // T22: the picker is ASYMMETRIC about upgrades vs downgrades.
    //
    //   Upgrade (desired.level < currentLevel, finer quality): always
    //   fetch. The user is looking at this mesh and it deserves sharper
    //   geometry.
    //
    //   Downgrade (desired.level > currentLevel, coarser quality): SKIP
    //   unless we're under FPS pressure (cooldownUntil was recently
    //   armed). Swapping to a coarser LOD saves work but we've already
    //   paid the download+GPU upload cost; unless FPS is struggling
    //   there's no reason to make the scene look worse.
    const underLoad = now < this.cooldownUntil + FPS_RECOVERY_MS * 2;
    for (const state of this.states) {
      if (this.inflight >= this.maxInflight) break;
      if (state.inFlightLevel !== -1) continue;

      const target = this.pickLodEntry(state, halfFovTan, viewportHeight);
      if (!target) continue;
      if (target.level === state.currentLevel) continue;

      const isUpgrade = target.level < state.currentLevel || state.currentLevel < 0;
      if (!isUpgrade && !underLoad) continue;

      this.scheduleFetch(state, target);
    }
  }

  /**
   * Wave 5 Task 1E — apply runtime tuning overrides. Any field left
   * undefined keeps its current (or default) value. Typically called
   * once right after construction using values pulled from the
   * PLOTEX_project root extension in scene.glb.
   */
  configure(opts: {
    updateIntervalMs?: number;
    maxInflight?: number;
    fpsTarget?: number;
    trisPerPixelTarget?: number;
    minScreenRadiusPx?: number;
  }): void {
    if (typeof opts.updateIntervalMs === 'number')
      this.updateIntervalMs = opts.updateIntervalMs;
    if (typeof opts.maxInflight === 'number')
      this.maxInflight = opts.maxInflight;
    if (typeof opts.fpsTarget === 'number')
      this.fpsTarget = opts.fpsTarget;
    if (typeof opts.trisPerPixelTarget === 'number')
      this.trisPerPixelTarget = opts.trisPerPixelTarget;
    if (typeof opts.minScreenRadiusPx === 'number')
      this.minScreenRadiusPx = opts.minScreenRadiusPx;
  }

  dispose(): void {
    this.disposed = true;
    for (const w of this.workers) w.terminate();
    this.workers.length = 0;
    this.states.length = 0;
    this.pending.clear();
  }

  /**
   * Wave 6 Task 5A — clear every cached artifact for a guid and re-queue
   * it for the picker. Intended for live-reload flows where the Blender
   * plugin just wrote a new ``mesh_<guid>.glb`` to disk and wants the
   * browser to pick up the fresh bytes without a full page reload.
   *
   * What it clears:
   *   - every ``${guid}_<level>`` entry in ``geometryCache`` (so the next
   *     request actually refetches the file instead of handing out the
   *     stale cached BufferGeometry)
   *   - every ``${guid}_<level>`` waiter list in ``inFlightByKey`` — any
   *     currently-in-flight fetch is orphaned (we can't cancel the
   *     workerpool job mid-parse, but when its response lands the
   *     ``onWorkerMessage`` handler will see the pending slot gone and
   *     drop the payload quietly)
   *   - the matching entries in ``failedKeys`` + ``failedKeyAttempts``
   *     so a file that was in the backoff park pen gets a fresh chance
   *
   * What it does to MeshState:
   *   - resets ``currentLevel`` to -1 and ``inFlightLevel`` to -1, so
   *     the picker's "desired != current" check fires on the next
   *     update tick and re-enqueues this mesh.
   *
   * What it does NOT do:
   *   - dispose the Mesh's current geometry. three.js will release it
   *     during the next ``applySharedGeometry`` swap once the fresh
   *     fetch lands. Disposing eagerly would produce a one-frame flash
   *     of the mesh rendering with a blank BufferGeometry.
   *
   * Idempotent and no-op-safe: calling it for a guid that isn't managed
   * (e.g. a brand-new mesh the artist just added) simply clears the
   * cache entries for that key prefix and returns.
   */
  invalidateGuid(guid: string): void {
    if (!guid) return;
    const prefix = `${guid}_`;

    // Drop every cache entry for this guid, disposing the BufferGeometry
    // only when no MeshState is still pointing at it. The picker will
    // re-populate the cache when the fresh fetch completes.
    const inUse = new Set<BufferGeometry>();
    for (const s of this.states) {
      if (s.meta.guid === guid) continue;
      // Defensive: other meshes might share a BufferGeometry object if a
      // future change widens cache keys across guids. Today they don't,
      // but checking is cheap.
      inUse.add(s.mesh.geometry);
    }
    for (const [key, geo] of Array.from(this.geometryCache.entries())) {
      if (!key.startsWith(prefix)) continue;
      if (!inUse.has(geo)) {
        try {
          geo.dispose();
        } catch {
          // three.js sometimes throws on already-disposed geometries —
          // ignore and keep going.
        }
      }
      this.geometryCache.delete(key);
    }

    // Orphan any in-flight waiter lists for this guid. We can't cancel
    // the worker postMessage round-trip, but when its result arrives
    // the initiator's Pending entry will have been removed by the time
    // the waiter lookup runs — the payload lands in the void.
    for (const key of Array.from(this.inFlightByKey.keys())) {
      if (key.startsWith(prefix)) this.inFlightByKey.delete(key);
    }

    // Clear the backoff pen so a previously failed fetch isn't still
    // parked when the artist drops a fresh (correct) file in place.
    for (const key of Array.from(this.failedKeys.keys())) {
      if (key.startsWith(prefix)) this.failedKeys.delete(key);
    }
    for (const key of Array.from(this.failedKeyAttempts.keys())) {
      if (key.startsWith(prefix)) this.failedKeyAttempts.delete(key);
    }

    // Reset the matching MeshState so the picker re-enqueues.
    for (const s of this.states) {
      if (s.meta.guid !== guid) continue;
      s.currentLevel = -1;
      s.inFlightLevel = -1;
    }
  }

  /**
   * Debug helper — return a human-readable summary of which (guid, level)
   * keys this session has touched. Combine with `ls public/chunks/` to
   * see how much of the on-disk catalog was actually used. Pre-T26 the
   * picker would always burn ~25 fetches on the L5 tier even though
   * scene.glb already shipped L5 inline; this report is how you verify
   * the inlineLevel honesty fix actually closed that gap.
   *
   * Example:
   *   mgr.usageReport()
   *   {
   *     uniqueKeysFetched: 14,
   *     totalFetchAttempts: 14,
   *     totalProgressiveMeshes: 34,
   *     levelsByCount: { '0': 11, '1': 2, '5': 1 },
   *     keysWithFailures: 0,
   *   }
   */
  usageReport(): {
    uniqueKeysFetched: number;
    totalFetchAttempts: number;
    totalProgressiveMeshes: number;
    levelsByCount: Record<string, number>;
    keysWithFailures: number;
    failedKeys: string[];
  } {
    const levelsByCount: Record<string, number> = {};
    let totalAttempts = 0;
    for (const [k, n] of this.fetchUsage) {
      totalAttempts += n;
      const level = k.slice(k.lastIndexOf('_') + 1);
      levelsByCount[level] = (levelsByCount[level] ?? 0) + 1;
    }
    return {
      uniqueKeysFetched: this.fetchUsage.size,
      totalFetchAttempts: totalAttempts,
      totalProgressiveMeshes: this.states.length,
      levelsByCount,
      keysWithFailures: this.failedKeys.size,
      failedKeys: Array.from(this.failedKeys.keys()),
    };
  }

  /**
   * Debug helper — returns the largest pairwise distance between the
   * given instance centers. Used at construction time to log whether
   * an InstancedMesh has meaningfully spread-out instances (palm row)
   * or essentially colocated ones (a pair of duplicated sconces).
   */
  private boundsSpread(points: Vector3[]): number {
    if (points.length < 2) return 0;
    let min = new Vector3(+Infinity, +Infinity, +Infinity);
    let max = new Vector3(-Infinity, -Infinity, -Infinity);
    for (const p of points) {
      if (p.x < min.x) min.x = p.x;
      if (p.y < min.y) min.y = p.y;
      if (p.z < min.z) min.z = p.z;
      if (p.x > max.x) max.x = p.x;
      if (p.y > max.y) max.y = p.y;
      if (p.z > max.z) max.z = p.z;
    }
    return max.distanceTo(min);
  }

  /* --- scoring & selection ---------------------------------------------- */

  /**
   * T9 coverage-based LOD picker.
   *
   * Computes the mesh's screen-space radius in pixels:
   *
   *   r_px = r_world / (dist * tan(fov/2)) * (viewportHeight / 2)
   *
   * then its approximate area (π * r_px²), multiplied by a triangle budget
   * per pixel. The LOD whose triangle count is closest to that target
   * (while staying within 1.5× for safety) wins.
   *
   * Behind-the-camera meshes and zero-radius degenerates fall through to
   * the coarsest available LOD so we don't waste bandwidth on things the
   * user can't see.
   */
  private pickLodEntry(
    state: MeshState,
    halfFovTan: number,
    viewportHeight: number
  ): PlotexLodEntry | null {
    const lods = state.meta.lods;
    if (!lods.length) return null;

    if (halfFovTan === 0 || viewportHeight === 0 || state.radius <= 0) {
      return lods[0]; // finest LOD for ortho / unknown projection
    }

    // InstancedMesh fix: if we cached per-instance world centers at
    // construction time, distance = nearest instance. That lets a row
    // of 52 palm trees upgrade the geometry as soon as the camera is
    // near ANY of them, instead of only when the camera is near the
    // geometry's local origin (which it never is in world space).
    let dist: number;
    if (state.instanceCenters && state.instanceCenters.length > 0) {
      let minDist = Infinity;
      for (const ic of state.instanceCenters) {
        const d = this.tmpCamPos.distanceTo(ic);
        if (d < minDist) minDist = d;
      }
      dist = Math.max(0.001, minDist);
    } else {
      dist = Math.max(0.001, this.tmpCamPos.distanceTo(state.center));
    }
    // Pull the near radius back by the mesh's own radius so meshes the
    // camera is INSIDE or touching still get the finest LOD.
    const effectiveDist = Math.max(0.001, dist - state.radius);

    // Screen-space radius in pixels.
    const screenRadiusPx =
      (state.radius / (effectiveDist * halfFovTan)) * (viewportHeight / 2);

    if (screenRadiusPx < this.minScreenRadiusPx) {
      // Too small to bother with — jump straight to the coarsest level.
      return lods[lods.length - 1];
    }

    const pixelArea = Math.PI * screenRadiusPx * screenRadiusPx;
    const targetTris = pixelArea * this.trisPerPixelTarget;

    // Walk LODs from fine → coarse and pick the first one whose triangle
    // count is <= targetTris. If none are small enough, pick the coarsest.
    // Walking fine-first means we bias toward quality when in doubt, which
    // avoids flicker at boundaries between buckets.
    let best: PlotexLodEntry = lods[lods.length - 1];
    let bestDiff = Infinity;
    for (const lod of lods) {
      const tris = lod.indexCount / 3;
      // Prefer the LOD closest to target, but never go more than 2× over.
      if (tris > targetTris * 2) continue;
      const diff = Math.abs(tris - targetTris);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = lod;
      }
    }

    // P1b: respect the author-set minLevel floor. `minLevel = 0` means
    // "no floor"; any non-zero value means the picker can never pick a
    // LOD coarser (higher level number) than that. Text and other
    // quality-sensitive meshes use this to guarantee they stay crisp
    // regardless of screen coverage.
    const minLevel = state.meta.minLevel;
    if (minLevel > 0 && best.level > minLevel) {
      // Pick the coarsest available LOD whose level ≤ minLevel — that's
      // the cheapest option that still meets the quality floor.
      let clamped: PlotexLodEntry | null = null;
      for (const lod of lods) {
        if (lod.level <= minLevel && (!clamped || lod.level > clamped.level)) {
          clamped = lod;
        }
      }
      if (clamped) best = clamped;
    }

    return best;
  }

  /* --- worker round-trip ------------------------------------------------- */

  /**
   * Ensure the LOD data for `(guid, level)` is parsed and loaded, and then
   * apply it to `state.mesh`. Has three fast paths:
   *
   *   1. cache hit        → apply immediately, no network, no worker
   *   2. in-flight shared → attach this state to the waiter list, no new fetch
   *   3. cache miss       → schedule a worker fetch
   *
   * This is the fix for the "same file fetched N times" bug the user saw in
   * DevTools. A scene with 50 palm instances previously fired 50 duplicate
   * requests for the same mesh_lod_*.glb; now it fires ONE.
   */
  private scheduleFetch(state: MeshState, lod: PlotexLodEntry): void {
    const key = `${state.meta.guid}_${lod.level}`;

    // Fast path 1: geometry already built — reuse the same BufferGeometry
    // object across instances so three.js uploads only one VBO to GPU.
    const cached = this.geometryCache.get(key);
    if (cached) {
      this.applySharedGeometry(state, cached, lod.level);
      return;
    }

    // Fast path 2: another mesh is already fetching this exact file.
    // Attach ourselves to its waiter list so we get the data when it
    // arrives. No new network request, no new worker message.
    const waiters = this.inFlightByKey.get(key);
    if (waiters) {
      state.inFlightLevel = lod.level;
      waiters.push(state);
      return;
    }

    // Backoff gate: if this key recently failed, don't even try yet.
    // The user reported missing chunk files producing a 200 OK + HTML
    // (Vite SPA fallback), which made the worker retry forever at
    // ~6 Hz. The picker is allowed to come back later; we just want
    // to stop pounding the network on a known-bad key.
    const retryAfter = this.failedKeys.get(key);
    if (retryAfter !== undefined && performance.now() < retryAfter) {
      return;
    }

    // Fast path 3: cache miss. Fire a worker fetch. Track this state as
    // the "initiator" and register the waiter list so later requests
    // for the same file can piggyback.
    const id = ++this.nextReqId;
    const worker = this.workers[this.nextWorker];
    this.nextWorker = (this.nextWorker + 1) % this.workers.length;

    state.inFlightLevel = lod.level;
    this.inflight++;

    this.pending.set(id, {
      meshState: state,
      requestedLevel: lod.level,
      resolve: () => {},
      reject: () => {},
    });
    this.inFlightByKey.set(key, [state]);
    this.fetchUsage.set(key, (this.fetchUsage.get(key) ?? 0) + 1);

    // P2b: at level 0, prefer the per-mesh chunk path written by the
    // Blender plugin (mesh_<guid>.glb) over the Node pipeline's
    // mesh_lod_0_<guid>.glb. Both contain the same geometry; using the
    // plugin path lets us drop the redundant L0 LOD file once P10
    // restructures the pipeline.
    const url =
      lod.level === 0 && state.meta.baseMeshPath
        ? '/chunks/' + state.meta.baseMeshPath
        : '/chunks/' + lod.path;

    worker.postMessage({ id, url });
  }

  private onWorkerMessage(e: MessageEvent<any>): void {
    const { id, ok, data, error } = e.data;
    const p = this.pending.get(id);
    if (!p) return;
    this.pending.delete(id);
    this.inflight--;

    const initiator = p.meshState;
    const key = `${initiator.meta.guid}_${p.requestedLevel}`;
    const waiters = this.inFlightByKey.get(key) ?? [initiator];
    this.inFlightByKey.delete(key);

    if (!ok) {
      // Park the key in failedKeys with an exponentially-growing
      // backoff so the picker can come back later (e.g. once the
      // missing chunk is dropped into /chunks/) without a 6 Hz
      // retry storm in the meantime. The first parse failure was
      // already logged with the URL — bump subsequent failures to
      // a quieter line so the console doesn't get flooded.
      const attempts = (this.failedKeyAttempts.get(key) ?? 0) + 1;
      this.failedKeyAttempts.set(key, attempts);
      const backoff = Math.min(
        FAIL_BACKOFF_MS * Math.pow(2, attempts - 1),
        FAIL_BACKOFF_MAX_MS
      );
      this.failedKeys.set(key, performance.now() + backoff);
      if (attempts === 1) {
        console.error(
          `[plotex-lod] fetch/parse failed for ${key}: ${error} ` +
          `(backing off ${backoff}ms)`
        );
      }
      for (const w of waiters) w.inFlightLevel = -1;
      return;
    }

    // Success — clear any prior backoff state for this key. The next
    // time a state requests the same key it'll go straight through
    // the cache fast path anyway, but clearing here keeps memory
    // tidy on long sessions.
    this.failedKeys.delete(key);
    this.failedKeyAttempts.delete(key);

    const parsed = data as ParsedLod;

    // Build the shared BufferGeometry ONCE from the parsed data, cache
    // it, and hand the SAME instance to every waiter. All mesh instances
    // end up pointing at the same geometry → three.js uploads the VBO
    // exactly once regardless of how many palms are in the scene.
    const geo = this.buildGeometryFromParsed(parsed);
    this.geometryCache.set(key, geo);

    for (const waiter of waiters) {
      try {
        this.applySharedGeometry(waiter, geo, p.requestedLevel);
      } catch (err) {
        console.warn('[plotex-lod] applyLod threw:', err);
        waiter.inFlightLevel = -1;
      }
    }
  }

  /**
   * Swap a mesh's geometry pointer to a shared cached BufferGeometry.
   * Safe to call across many Mesh instances for the same cached key —
   * three.js handles geometry sharing correctly.
   *
   * CRITICAL (fetch-oscillation fix): do NOT mutate `state.center` after
   * the swap. Every LOD level has a slightly different vertex set → a
   * slightly different bounding-sphere center. Refreshing state.center
   * from the swapped-in geometry makes the picker's coverage math drift
   * by ~half a meter per swap, which on borderline LOD boundaries
   * oscillates between two levels and retriggers fetches forever.
   * The mesh's world POSITION doesn't change when you swap LODs —
   * only the triangle count does — so we lock in state.center once at
   * construction time and trust it for the lifetime of the manager.
   */
  private applySharedGeometry(
    state: MeshState,
    geo: BufferGeometry,
    level: number
  ): void {
    const old = state.mesh.geometry;
    state.mesh.geometry = geo;
    // Only dispose the old geometry if it's not itself a shared cached
    // entry. We compare via Map reverse lookup; for cacheless inline
    // geometry this always disposes.
    let isShared = false;
    for (const v of this.geometryCache.values()) {
      if (v === old) { isShared = true; break; }
    }
    if (!isShared && old && old !== geo) old.dispose();

    state.currentLevel = level;
    state.inFlightLevel = -1;
    // state.center intentionally NOT touched — see docstring.
  }

  /* --- geometry swap ----------------------------------------------------- */

  /**
   * Build a fresh BufferGeometry from the worker's parsed LOD payload.
   *
   * We only do this ONCE per unique (guid, level) pair; the resulting
   * BufferGeometry is then reused across every Mesh instance sharing
   * that guid via `applySharedGeometry`, so three.js uploads the VBO
   * to the GPU exactly once for that LOD.
   *
   * The pipeline writes LOD files whose position encoding matches
   * scene.glb's local frame for the same mesh, so the attribute buffers
   * drop straight in: three.js's existing `target.matrixWorld`
   * transforms them to the correct world positions with no per-instance
   * math here.
   */
  private buildGeometryFromParsed(data: ParsedLod): BufferGeometry {
    const geo = new BufferGeometry();

    const posAttr = data.attributes.POSITION;
    const posArr = posAttr.array as Int16Array | Float32Array;
    geo.setAttribute(
      'position',
      new BufferAttribute(posArr as any, posAttr.itemSize, posAttr.normalized)
    );

    const nAttr = data.attributes.NORMAL;
    if (nAttr) {
      const a = nAttr.array as Int16Array | Float32Array;
      geo.setAttribute(
        'normal',
        new BufferAttribute(a as any, nAttr.itemSize, nAttr.normalized)
      );
    }

    const uvAttr = data.attributes.TEXCOORD_0;
    if (uvAttr) {
      const a = uvAttr.array as Int16Array | Float32Array;
      geo.setAttribute(
        'uv',
        new BufferAttribute(a as any, uvAttr.itemSize, uvAttr.normalized)
      );
    }

    geo.setIndex(new BufferAttribute(data.indices, 1));
    geo.computeBoundingBox();
    geo.computeBoundingSphere();
    return geo;
  }
}
